import { useCallback, useMemo, useRef, useState, RefObject } from 'react';
import {
  DataTableBaseColumn,
  DataTableRow,
  DataTableSortStateProps,
  DataTableVariant,
} from './TableTypes';
import { SortOrder } from '../types';
import { ACTION_BUTTONS_KEY, DataTableId } from './utils';
import { useInitializeMeasureColumnWidth } from './custom-columns-table/useInitializeMeasureColumnWidth';
import { TableHeadRow } from './TableHeadRow';

export interface UseVirtualScrollTableProps<TRow extends DataTableRow> {
  columns: DataTableBaseColumn[];
  rows: TRow[];
  containerRef?: React.RefObject<HTMLDivElement>;
  onSortChange?: (props: { sortBy: string; sortOrder: SortOrder }) => void;
  defaultSortState?: DataTableSortStateProps;
  tableId: DataTableId;
  variant?: DataTableVariant;
  enabled?: boolean;
}

export interface UseVirtualScrollTableReturn {
  sortState: DataTableSortStateProps | undefined;
  noActionColumns: DataTableBaseColumn[];
  tableRef: RefObject<any>;
  internalColumnWidths: Record<string, number> | undefined;
  sort: (columnName: string) => void;
  renderHeader: () => JSX.Element;
}

export function useVirtualScrollTable<TRow extends DataTableRow>({
  columns,
  rows,
  containerRef,
  onSortChange,
  defaultSortState,
  tableId,
  variant = DataTableVariant.medium,
  enabled = true,
}: UseVirtualScrollTableProps<TRow>): UseVirtualScrollTableReturn {
  const [sortState, setSortState] = useState<DataTableSortStateProps | undefined>(
    enabled ? defaultSortState : undefined,
  );

  const noActionColumns = useMemo(
    () =>
      enabled
        ? columns.filter((column) => !column.hidden && column.field !== ACTION_BUTTONS_KEY)
        : [],
    [columns, enabled],
  );

  const _tableRef: RefObject<any> = useRef(null);
  const tableRef = containerRef ?? _tableRef;

  const sort = useCallback(
    (columnName: string) => {
      if (!enabled) return;

      const newState = {
        sortBy: columnName,
        sortOrder: sortState?.sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC,
      };

      setSortState(newState);
      onSortChange && onSortChange(newState);
    },
    [onSortChange, sortState?.sortOrder, enabled],
  );

  const [internalColumnWidths, setInternalColumnWidths] = useState<
    Record<string, number> | undefined
  >();
  const [initialWidthsMeasured, setInitialWidthsMeasured] = useState(false);
  const internalColumnWidthsRef = useRef<Record<string, number>>({});

  useInitializeMeasureColumnWidth({
    filteredAndSortedColumns: enabled ? noActionColumns : [],
    rows: enabled ? rows : [],
    columnWidthsRef: internalColumnWidthsRef,
    setColumnWidths: setInternalColumnWidths,
    initialWidthsMeasured,
    setInitialWidthsMeasured,
    tableId: 'internal_calculations' as any,
  });

  const renderHeader = useCallback(() => {
    if (!enabled) {
      return <div />;
    }

    return (
      <TableHeadRow
        columns={noActionColumns}
        stickyHeader
        variant={variant}
        sort={sort}
        sortState={sortState}
        tableId={tableId}
      />
    );
  }, [noActionColumns, tableId, sort, sortState, variant, enabled]);

  return {
    sortState,
    noActionColumns,
    tableRef,
    internalColumnWidths,
    sort,
    renderHeader,
  };
}
